import type { CustomerCatalogueOption } from "@shared-core/data/customer-api-types";
import { customerLabelOf } from "@shared-core/domain/customer-option-wording";
import { Badge } from "../../app/components/primitives/badge";
import {
  RadioGroup,
  RadioGroupTileItem,
} from "../../app/components/primitives/radio-group";
import { cn } from "../../app/components/primitives/utils";
import {
  hasOptionGroupHint,
  OptionGroupHint,
  optionGroupHintId,
} from "./catalogue-item/OptionHint";

// One option's choices, shown at once as selectable tiles (Brad's portal
// feedback, 2026-10-02: "I would prefer them not being drop downs").
// RadioGroupTileItem carries real radio semantics (role="radio"/aria-checked,
// one selected per group, arrow-key navigation from Radix) -- it just LOOKS
// like a tile instead of a dot-plus-label.
//
// 2026-10-12, Brad: the tile page was too long, so this is only for groups of
// up to TILE_MAX_OPTIONS choices (catalogue-item/option-groups.ts); a longer
// group is an OptionSelect dropdown.
//
// The dictionary's customer wording replaces the labels: the group's explanation
// sits under its heading, and a choice's own explanation is the small line
// inside its tile.
export function OptionTileGroup({
  option,
  value,
  onChange,
  unavailableCodes,
  mostPopularCode,
  className,
}: {
  option: CustomerCatalogueOption;
  value: string;
  onChange: (code: string) => void;
  /** Choices the rest of the selection rules out (R28), shown disabled. */
  unavailableCodes?: ReadonlySet<string>;
  /** The choice to badge "Most popular"; never shown on an unavailable choice. */
  mostPopularCode?: string;
  className?: string;
}) {
  return (
    <div className={cn("space-y-2", className)}>
      <label className="text-sm font-medium" id={`option-${option.key}-label`}>
        {customerLabelOf(option)}
      </label>
      <OptionGroupHint option={option} />
      <RadioGroup
        value={value}
        onValueChange={onChange}
        aria-labelledby={`option-${option.key}-label`}
        aria-describedby={
          hasOptionGroupHint(option) ? optionGroupHintId(option) : undefined
        }
        className="grid grid-cols-[repeat(auto-fit,minmax(8.5rem,1fr))] gap-2.5"
      >
        {option.choices.map((choice) => {
          const isUnavailable = unavailableCodes?.has(choice.code) ?? false;
          return (
            <RadioGroupTileItem
              key={choice.code}
              value={choice.code}
              disabled={isUnavailable}
              className="relative h-auto rounded-lg px-3 py-2.5 text-left"
            >
              {mostPopularCode === choice.code && !isUnavailable && (
                <Badge className="absolute -top-2 right-2 px-1.5 py-0 text-[10px]">
                  Most popular
                </Badge>
              )}
              {customerLabelOf(choice)}
              {choice.customer_hint && (
                <span className="mt-1 block text-xs font-normal text-muted-foreground">
                  {choice.customer_hint}
                </span>
              )}
            </RadioGroupTileItem>
          );
        })}
      </RadioGroup>
    </div>
  );
}
